import { useDispatch, useSelector } from "react-redux";
import useQuestionState from "../useQuestionStateHook";

export const selectCannedResponseState = (options, response) => state => {
  return options.reduce((obj, option) => {
    const {
      response_id: responseId,
      value,
      label,
      sort_order: sortOrder,
      specify_detail: isSpecifyDetail
    } = option;

    const savedResponse = response?.[responseId] || {};

    obj[responseId] = {
      checked: typeof savedResponse.value !== "undefined",
      value,
      label,
      sortOrder,
      isSpecifyDetail: Boolean(isSpecifyDetail),
      detail: savedResponse.detail
    };
    return obj;
  }, {});
};

export default function useCannedResponseStateHook({ options, ...props }) {
  const { question_type: questionType } = props;
  const {
    isValidResponse,
    response,
    userSkipped,
    saveResponse
  } = useQuestionState(props);

  const state = useSelector(selectCannedResponseState(options, response));
  const dispatch = useDispatch();

  const selectedResponses = Object.entries(state).filter(
    ([, { checked }]) => checked
  );

  // get more detailed info from selected responses for canned response select
  function getSpecifyDetail(selectedResponses) {
    if (selectedResponses[0][1].isSpecifyDetail === true) {
      return true;
    } else {
      return false;
    }
  }
  function getSavedDetail(selectedResponses) {
    return selectedResponses[0][1].detail;
  }
  function getResponseId(selectedResponses) {
    return selectedResponses[0][0];
  }

  // initialize extra decision flags for canned select ui
  var cannedSelect = {
    isSpecifyDetail: false,
    detail: "No Selection",
    responseId: "No Selection"
  };

  // set flags for canned select questions
  if (
    questionType === "CANNED_RESPONSE_SELECT" &&
    selectedResponses !== undefined &&
    selectedResponses.length !== 0
  ) {
    cannedSelect = {
      isSpecifyDetail: getSpecifyDetail(selectedResponses),
      detail: getSavedDetail(selectedResponses),
      responseId: getResponseId(selectedResponses)
    };
  }

  // get counts
  const totalCount = Object.keys(state).length;
  const checkedCount = selectedResponses.length;

  const selectAllStatus =
    checkedCount === 0 ? "NONE" : checkedCount === totalCount ? "ALL" : "SOME";

  const toggleSelectAll = () => {
    const updatedResponses = Object.entries(state).reduce(
      (acc, [responseId, response]) => ({
        ...acc,
        [responseId]: {
          ...response,
          checked: checkedCount !== totalCount
        }
      }),
      {}
    );

    dispatch(
      saveResponse(
        Object.entries(updatedResponses).reduce(
          (acc, [responseId, { value, sortOrder, isSpecifyDetail }]) => ({
            ...acc,
            [responseId]: {
              value,
              ...(questionType === "CANNED_RESPONSE_CHECKBOX" && {
                sort_order: sortOrder
              }),
              ...(isSpecifyDetail && { detail: "" })
            }
          }),
          {}
        )
      )
    );
  };

  const toggleSelect = responseId => {
    const currentResponse = state[responseId];
    const updatedResponses = {
      ...state,
      [responseId]: {
        ...currentResponse,
        checked: !currentResponse.checked
      }
    };

    dispatch(
      saveResponse(
        Object.entries(updatedResponses)
          .filter(([, { checked }]) => checked)
          .reduce(
            (
              acc,
              [responseId, { value, sortOrder, isSpecifyDetail, detail = "" }]
            ) => ({
              ...acc,
              [responseId]: {
                value,
                ...(questionType === "CANNED_RESPONSE_CHECKBOX" && {
                  sort_order: sortOrder
                }),
                ...(isSpecifyDetail && { detail })
              }
            }),
            {}
          )
      )
    );
  };

  const setSelected = clickedResponseId => {
    const updatedResponses = Object.entries(state).reduce(
      (acc, [responseId, response]) => ({
        ...acc,
        [responseId]: {
          ...response,
          checked: responseId === clickedResponseId
        }
      }),
      {}
    );

    dispatch(
      saveResponse(
        Object.entries(updatedResponses)
          .filter(([, { checked }]) => checked)
          .reduce(
            (
              acc,
              [responseId, { value, sortOrder, isSpecifyDetail, detail = "" }]
            ) => ({
              ...acc,
              [responseId]: {
                value,
                ...(questionType === "CANNED_RESPONSE_CHECKBOX" && {
                  sort_order: sortOrder
                }),
                ...(isSpecifyDetail && { detail })
              }
            }),
            {}
          )
      )
    );
  };

  const setDetail = (responseId, detail) => {
    dispatch(
      saveResponse(
        Object.entries(state)
          .filter(([, { checked }]) => checked)
          .reduce(
            (
              acc,
              [
                currentResponseId,
                {
                  value,
                  sortOrder,
                  isSpecifyDetail,
                  detail: currentDetail = ""
                }
              ]
            ) => ({
              ...acc,
              [currentResponseId]: {
                value,
                ...(questionType === "CANNED_RESPONSE_CHECKBOX" && {
                  sort_order: sortOrder
                }),
                ...(isSpecifyDetail && {
                  detail:
                    currentResponseId === responseId ? detail : currentDetail
                })
              }
            }),
            {}
          )
      )
    );
  };

  return {
    isValidResponse,
    state,
    selectedResponses,
    userSkipped,
    toggleSelect,
    setSelected,
    setDetail,
    selectAllStatus,
    toggleSelectAll,
    cannedSelect
  };
}
